import React from "react";
import { RouteComponentProps } from "react-router";
import { request, handleValidationErrors } from "../helpers/utilities";
import { Spin, message, Button, Row, Col } from "antd";
import Card from "../components/Card";
import { AxiosResponse } from "axios";
import { FormInstance } from "antd/lib/form/Form";
import BaseForm from "../components/Form/BaseForm";
import { APIError, Trustee } from "../helpers/shared-interfaces";
import * as Sentry from "@sentry/browser";
import { GET_TRUSTEE, UPDATE_TRUSTEE } from "../gql/trustees";
import { generateDisplayErrorMessage } from "../helpers/utilities";
import {
  PAGE_COLUMNS,
  TRUSTEE_FORM_FIELD_NAMES,
  trusteeFormFields,
} from "../components/TrusteeForm";

interface State {
  id: string | number;
  isLoading: boolean;
}

interface RouteParams {
  id: string;
}

export default class EditTrustee extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = { isLoading: true, id: "" };
    this.redirectToTrusteePage = this.redirectToTrusteePage.bind(this);
    this.updateTrustee = this.updateTrustee.bind(this);
    this.getTrustee = this.getTrustee.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  async updateTrustee(): Promise<void> {
    interface Response {
      data?: {
        updateTrustee?: {
          trustee?: {
            name: string;
          };
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(
        TRUSTEE_FORM_FIELD_NAMES,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_TRUSTEE,
        variables: { ...data, id: this.state.id },
      });
      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data.updateTrustee &&
        response.data.data.updateTrustee.trustee
      ) {
        message.success("Trustee updated sucessfully");
        this.redirectToTrusteePage();
      } else {
        message.error("Failed to update trustee");
      }
    } catch (error) {
      message.error(`Failed to update trustee - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  redirectToTrusteePage(): void {
    this.props.history.push(`/trustees/${this.state.id}`);
  }

  async getTrustee(): Promise<void> {
    this.setState({ isLoading: true });
    interface Response {
      data?: {
        trustee?: Trustee;
      };
      errors?: Record<string, unknown>[];
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_TRUSTEE,
        variables: { id: this.state.id },
      });

      this.setState({ isLoading: false });

      const trustee = response?.data?.data?.trustee;

      if (!trustee) {
        message.error("Trustee not found.");
        return;
      }

      this.reference.current?.setFieldsValue({
        name: trustee.name,
        postalAddress: trustee.postalAddress || "",
        streetName: trustee.streetName || "",
        location: trustee.location || "",
      });
    } catch (error) {
      message.error(
        `An error occurred while fetching trustee. - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      this.setState({ isLoading: false });
    }
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, () => {
      this.getTrustee();
    });
  }

  render(): JSX.Element | null {
    const { isLoading } = this.state;

    return (
      <Card header="">
        <div className="create-trustee-container" style={{}}>
          <Spin spinning={isLoading}>
            <BaseForm
              size="large"
              reference={this.reference}
              layout="vertical"
              fields={trusteeFormFields(PAGE_COLUMNS)}
              fieldsToHide={[]}
            />
            <Row gutter={16} style={{ marginTop: 32 }}>
              <Col>
                <Button
                  type="primary"
                  className="base-button"
                  onClick={this.updateTrustee}
                  loading={isLoading}
                  data-cy="test-update-trustee-button"
                >
                  Update Trustee
                </Button>
              </Col>
              <Col>
                <Button
                  onClick={this.redirectToTrusteePage}
                  style={{ height: "33px" }}
                  data-cy="test-cancel-trustee-button"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Spin>
        </div>
      </Card>
    );
  }
}
